/**
* Verified legal facts used across the site.
*
* Every entry MUST cite a primary source (government / treaty depositary)
* and record the date a human last verified it. Never add a number here
* that cannot be traced to one of the listed sources.
*/

export type FactSource = {
  label: string;
  url: string;
};

/**
* Thailand & the 1961 Hague Apostille Convention.
*
* Verified 2026-07-30 against:
* - HCCH news, "Thailand accedes to the 1961 Apostille Convention" (30 June 2026)
* - Ministry of Foreign Affairs of Thailand, Department of Consular Affairs
*
* Thailand deposited its instrument of accession on 30 June 2026. An
* eight-month objection period for existing Contracting States follows,
* after which the Convention enters into force for Thailand on
* 28 February 2027. Until that date Thailand does NOT issue Apostilles;
* the legalisation chain remains Notary/issuing authority -> MFA
* (Legalisation Division) -> destination embassy where required.
*/
export const APOSTILLE_STATUS = {
  /** Deposit of the instrument of accession. */
  accessionDateISO: "2026-06-30",
  accessionDateTh: "30 มิถุนายน 2569",
  /** Entry into force for Thailand. */
  inForceDateISO: "2027-02-28",
  inForceDateTh: "28 กุมภาพันธ์ 2570",
  /** Last human verification of this block. */
  verifiedOnISO: "2026-07-30",
  verifiedOnTh: "30 กรกฎาคม 2569",
  /** Authority that legalises documents today and will issue Apostilles later. */
  authorityTh: "กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ",
  authorityEn: "Legalisation Division, Department of Consular Affairs, MFA Thailand",
  sources: [
    {
      label: "HCCH — Thailand accedes to the 1961 Apostille Convention (30 June 2026)",
      url: "https://www.hcch.net/en/news-archive/details/?varevent=1158",
    },
    {
      label: "กระทรวงการต่างประเทศ — การภาคยานุวัติอนุสัญญา Apostille",
      url: "https://www.mfa.go.th/en/content/apostille-convention-eng",
    },
    {
      label: "กรมการกงสุล — งานรับรองเอกสาร (นิติกรณ์)",
      url: "https://consular.mfa.go.th/th/publicservice/legalization",
    },
  ] satisfies FactSource[],
} as const;

/** True while Thailand still cannot issue an Apostille (before entry into force). */
export function apostilleInTransition(now: Date = new Date()): boolean {
  return now < new Date(APOSTILLE_STATUS.inForceDateISO);
}

/** One-sentence, always-accurate summary of the current legalisation chain. */
export const APOSTILLE_STATUS_SUMMARY_TH =
  `ปัจจุบันประเทศไทยยังไม่ออกตรา Apostille — ไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 เมื่อ ${APOSTILLE_STATUS.accessionDateTh} และอนุสัญญาจะมีผลบังคับใช้กับไทยในวันที่ ${APOSTILLE_STATUS.inForceDateTh}. จนถึงวันดังกล่าว เอกสารไทยที่จะใช้ต่างประเทศยังต้องผ่านสายรับรองเดิม คือ ผู้ออกเอกสาร/Notary → รับรองนิติกรณ์ที่กรมการกงสุล → รับรองสถานทูตประเทศปลายทาง (เฉพาะประเทศที่ยังกำหนด)`;

export const APOSTILLE_STATUS_SUMMARY_EN =
  `Thailand does not issue Apostilles yet. It deposited its instrument of accession to the 1961 Hague Apostille Convention on 30 June 2026, and the Convention enters into force for Thailand on 28 February 2027. Until then, Thai documents for overseas use follow the existing chain: issuing authority / notary → MFA legalisation → destination embassy where still required.`;
